import { Navigate, useLocation } from 'react-router';
import AuthUser from '../../common/utils/AuthUser';
import { PATH_PAGE, PATH_APP } from '../../routes/paths';
import { atLeastManager, atLeastAdmin } from '@/models/common';
import { checkPathFeatureFlag } from '@/common/utils/featureFlags';
import { useAnalytics } from '@restore-hw/analytics';
import { useEffect } from 'react';

type Props = {
  children: React.ReactNode;
};

function hasRequiredRole(userRoles, requiredRoles) {
  return userRoles.some(userRole => requiredRoles.includes(userRole.role));
}

function AuthProtect({ children }: Props) {
  const loggedInUser = AuthUser.getAuthUser();
  const userRoles = AuthUser.getRoles();
  const location = useLocation();
  const pathname = location.pathname;
  const isLoginPage = pathname.includes('/auth');
  const isManagerPage = pathname.includes('/manager');
  const isAdminPage = pathname.includes('/admin');
  const { resetAnalytics } = useAnalytics();

  const isUnauthorized =
    (isManagerPage && !hasRequiredRole(userRoles, atLeastManager())) ||
    (isAdminPage && !hasRequiredRole(userRoles, atLeastAdmin()));

  const isFeatureEnabled = checkPathFeatureFlag(pathname);

  useEffect(() => {
    if (!loggedInUser && resetAnalytics) {
      resetAnalytics();
    }
  }, [resetAnalytics, loggedInUser]);

  if (!loggedInUser && isLoginPage) {
    return children;
  } else if (!loggedInUser) {
    const redirect = encodeURIComponent(pathname);
    const loginPath =
      pathname == '/'
        ? PATH_PAGE.auth.login
        : `${PATH_PAGE.auth.login}?redirect=${redirect}`;
    return <Navigate to={loginPath} replace />;
  } else if (isUnauthorized || !isFeatureEnabled) {
    return (
      <Navigate
        to={{
          pathname: PATH_APP.appointment.root,
          search: location.search
        }}
        replace
      />
    );
  } else if (loggedInUser && !loggedInUser.store) {
    return <Navigate to={PATH_PAGE.auth.login} replace />;
  } else if (loggedInUser && isLoginPage) {
    return (
      <Navigate
        to={{
          pathname: PATH_APP.appointment.root,
          search: location.search
        }}
        replace
      />
    );
  } else if (loggedInUser && loggedInUser.store) {
    return children;
  }
  return false;
}

export default AuthProtect;
